import React, { useState, useEffect } from 'react'
import Header from '../../layouts/Header'
import { Col, Row, Card, Table, Button, Form, Modal, Spinner, Badge, InputGroup, FormControl } from 'react-bootstrap'
import { useSelector } from 'react-redux';
import makeAnimated from "react-select/animated";
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { PlusIcon, Wallet, EyeIcon, CreditCard, Store, Search, Filter } from 'lucide-react';
import { FileEarmarkSpreadsheet } from 'react-bootstrap-icons';

const PettyCash = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const animatedComponents = makeAnimated();

    // Custom styles for react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            minHeight: '38px',
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                borderColor: '#86b7fe',
            },
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? (skin === 'dark' ? '#3a4155' : '#e9ecef')
                : (skin === 'dark' ? '#2a3042' : 'white'),
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                backgroundColor: skin === 'dark' ? '#3a4155' : '#e9ecef',
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
        }),
        input: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
    };

    const { access } = useSelector((state) => state.login);
    const { email } = useSelector((state) => state.login);
    const [pettyCashAccess, setPettyCashAccess] = useState([]);
    const [pettyCashTransactions, setPettyCashTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [outlets, setOutlets] = useState([]);
    const [outletsBalance, setOutletsBalance] = useState([]);
    const [filteredOutletsBalance, setFilteredOutletsBalance] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [creditOutletName, setCreditOutletName] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'transactions'
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [transactionId, setTransactionId] = useState('');
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [outletCurrentBalance, setOutletCurrentBalance] = useState(0);
    const [outletId, setOutletId] = useState('');
    const [outletName, setOutletName] = useState('');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    // Filter states
    const [outletFilter, setOutletFilter] = useState('ACTIVE'); // 'ACTIVE', 'INACTIVE', 'ALL'
    const [searchQuery, setSearchQuery] = useState('');

    // Crediting Section 
    const [inputData, setInputData] = useState({
        outlet: '',
        amount: '',
        remark: '',
        invoice: null
    });
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("PettyCash:")
            );
            setPettyCashAccess(filteredAccess);
        }
    }, [access]);

    const accessToken = localStorage.getItem('authToken');

    // Get outlets with their current petty cash balance
    const getOutletsBalance = async () => {
        setLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };
            const response = await axios.get('outlet/petty-cash/cash-balance', { headers });

            if (response?.data?.status === true) {
                console.log("Outlets balance response:", response);
                const outletsData = response?.data?.data?.outletsCashBalance || [];
                setOutletsBalance(outletsData);
                // Apply filters after fetching data
                applyFilters(outletsData, outletFilter, searchQuery);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = () => {
        setDownloadBtnLoading(true);
        getTransactions(outletId, true);
    }

    const getTransactions = async (outletId = null, download) => {
        setLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const outletParam = outletId || selectedOutlet?.value;

            if (!outletParam) {
                toast.error('Please select an outlet first', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
                setLoading(false);
                return;
            }

            let url = `outlet/petty-cash/transactions?outlet=${outletParam}`;

            if (download) {
                url += `&isDownload=${download}`
            }

            const response = await axios.get(url, {
                headers,
                responseType: download ? 'blob' : 'json'
            })
            if (download) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `Petty Cash Transactions For ${outletName}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);

                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            }
            if (response?.data?.status === true) {
                console.log("Response of petty cash transactions :", response);
                setPettyCashTransactions(response?.data?.data?.outletPettyCashTransactions || []);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    }

    const getOutlets = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                const outletsData = response?.data?.data?.outlets || [];
                const outletOptions = outletsData.map(outlet => ({
                    value: outlet._id,
                    label: outlet.outletName || outlet.name || 'Unknown Outlet'
                }));
                setOutlets(outletOptions);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    // Apply filters to outlets
    const applyFilters = (outletsData, statusFilter, searchText) => {
        let filtered = [...outletsData];

        // Apply status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter(outlet => {
                const status = outlet?.outlet?.status || outlet?.status || 'ACTIVE';
                return status === statusFilter;
            });
        }

        // Apply search filter
        if (searchText.trim() !== '') {
            const query = searchText.toLowerCase();
            filtered = filtered.filter(outlet => {
                const outletName = outlet?.outlet?.name || outlet?.name || '';
                return outletName.toLowerCase().includes(query);
            });
        }

        setFilteredOutletsBalance(filtered);
    };

    // Handle status filter change
    const handleStatusFilterChange = (status) => {
        setOutletFilter(status);
        applyFilters(outletsBalance, status, searchQuery);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        applyFilters(outletsBalance, outletFilter, value);
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
        applyFilters(outletsBalance, outletFilter, '');
    };

    const handleCreditPettyCash = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            };

            const formData = new FormData();
            formData.append('outlet', inputData.outlet);
            formData.append('amount', inputData.amount);
            formData.append('remark', inputData.remark);
            if (inputData.invoice) {
                formData.append('invoice', inputData.invoice);
            }

            const response = await axios.post('outlet/petty-cash/credit', formData, { headers });

            if (response?.data?.status === true) {
                toast.success('Petty cash credited successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });

                // Reset form and close modal
                setInputData({
                    outlet: '',
                    amount: '',
                    remark: '',
                    invoice: null
                });
                setShowCreditModal(false);

                // Refresh outlets balance and transactions
                getOutletsBalance();
                if (inputData.outlet === selectedOutlet?.value) {
                    getTransactions(inputData.outlet);
                }
            }
        } catch (error) {
            console.error('Credit petty cash error:', error);
            let errorMessage = 'Failed to credit petty cash';
            if (error.response && error.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewTransactions = (outlet) => {
        setSelectedOutlet({
            value: outlet?.outlet?._id || outlet._id,
            label: outlet?.outlet?.name || outlet.name
        });
        setCurrentBalance(outlet?.cash_balance);
        setViewMode('transactions');
        setOutletId(outlet?.outlet?._id || outlet._id);
        setOutletName(outlet?.outlet?.name || outlet.name)
        getTransactions(outlet?.outlet?._id || outlet._id);
        getCurrentBalance(outlet?.outlet?._id || outlet._id)
    };

    const getCurrentBalance = async (outId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axios.get(`outlet/petty-cash/cash-balance?outlet=${outId}`, { headers });
            console.log("Response of Current Balance :", response);
            setOutletCurrentBalance(response?.data?.data?.outletPettyCashCurrentBalance?.cash_balance);
        } catch (error) {
            console.log("Error While Fetching Current Balance :", error);
        }
    }

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedOutlet(null);
        setPettyCashTransactions([]);
        setCurrentBalance(0);
    };

    const handleCreditOutlet = (outlet) => {
        console.log("Open Credit Modal :", outlet);
        setCreditOutletName(outlet?.outlet?.name);
        setInputData(prev => ({
            ...prev,
            outlet: outlet?.outlet?._id || outlet._id
        }));
        setShowCreditModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCloseCreditModal = (e) => {
        setShowCreditModal(false);
        setInputData({
            amount: '',
            invoice: null,
            outlet: '',
            remark: ''
        })
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const forbiddenTypes = ["audio/mpeg", "video/mp4", "image/gif"]; // mp3, mp4, gif MIME types

        if (forbiddenTypes.includes(file.type)) {
            alert("Invalid file type! MP3, MP4 and GIF files are not allowed.");
            e.target.value = ""; // reset file input
            return;
        }

        setInputData(prev => ({
            ...prev,
            invoice: file
        }));
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch (error) {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const getTransactionTypeBadge = (type) => {
        const variants = {
            CREDIT: 'success',
            DEBIT: 'danger'
        };
        return (
            <Badge bg={variants[type] || 'secondary'}>
                {type}
            </Badge>
        );
    };

    const getAmountDisplay = (transaction) => {
        const isCredit = transaction.type === 'CREDIT';
        const amountClass = isCredit ? 'text-success fw-bold text-end' : 'text-danger fw-bold text-end';
        const sign = isCredit ? '+' : '-';

        return (
            <td className={amountClass}>
                {sign} ₹ {formatCurrency(transaction.amount)}
            </td>
        );
    };

    const getAddedByDisplay = (transaction) => {
        if (transaction.adminCreatedBy) {
            return 'Admin';
        } else if (transaction.outletUserCreatedBy) {
            return 'Outlet User';
        } else if (transaction.addedBy?.email) {
            return transaction.addedBy.email;
        }
        return '-';
    };

    const getBalanceVariant = (balance) => {
        if (balance > 1) return 'success';
        return 'danger';
    };

    const handleDelete = (data) => {
        setOpenDeleteModal(true);
        console.log("Delete Modal Data :", data);
        setTransactionId(data?._id)
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
        setTransactionId('');
    }

    const handleSubmitDelete = async (e) => {
        e.preventDefault();
        setIsDeleteLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const data = {
                _id: transactionId,
            };

            const response = await axios.delete(`outlet/petty-cash/transactions`, { data, headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
                getTransactions();
                getCurrentBalance(outletId)
                setOpenDeleteModal(false);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } finally {
            setIsDeleteLoading(false);
        }
    }

    // Get status badge
    const getStatusBadge = (status) => {
        const variants = {
            ACTIVE: 'success',
            INACTIVE: 'danger',
            SUSPENDED: 'warning'
        };
        return (
            <Badge bg={variants[status] || 'secondary'} className="ms-2">
                {status}
            </Badge>
        );
    };

    useEffect(() => {
        getOutletsBalance();
        getOutlets();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div className="container shadow-sm" style={{ backgroundColor: "white" }}>
                    <Row className="pt-3">
                        <Col>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center">
                                    <Wallet className="me-3" size={26} color="#0d6efd" />
                                    <h4 className="fw-semibold mb-0">PETTY CASH MANAGEMENT</h4>
                                </div>
                                {viewMode === 'transactions' && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleBackToList}
                                        size="sm"
                                    >
                                        ← Back to Balances
                                    </Button>
                                )}
                                {viewMode === 'list' && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleBackToList}
                                        size="sm"
                                        className="invisible"
                                    >
                                        ← Back to Balances
                                    </Button>
                                )}
                            </div>
                        </Col>
                        <hr />
                    </Row>

                    {viewMode === 'list' ? (
                        /* Outlets List with Balances */
                        <React.Fragment>
                            {/* <Row className="mb-2">
                                <Col>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">Outlet Balances</h5>
                                        <Button
                                            variant="outline-primary"
                                            onClick={getOutletsBalance}
                                            disabled={loading}
                                            size="sm"
                                        >
                                            <i className="fas fa-sync-alt me-2"></i>
                                            {loading ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row> */}

                            {/* Filters Section */}
                            <Row className="mb-1">
                                <Col md={6} lg={4} className="mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <Filter size={18} className="me-2 text-muted" />
                                        <h6 className="mb-0">Status Filter</h6>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant={outletFilter === 'ACTIVE' ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('ACTIVE')}
                                            className="flex-fill"
                                        >
                                            Active
                                        </Button>
                                        <Button
                                            variant={outletFilter === 'INACTIVE' ? 'danger' : 'outline-danger'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('INACTIVE')}
                                            className="flex-fill"
                                        >
                                            Inactive
                                        </Button>
                                        <Button
                                            variant={outletFilter === 'ALL' ? 'secondary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleStatusFilterChange('ALL')}
                                            className="flex-fill"
                                        >
                                            All
                                        </Button>
                                    </div>
                                </Col>
                                <Col md={6} lg={8} className="mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <Search size={18} className="me-2 text-muted" />
                                        <h6 className="mb-0">Search Outlet</h6>
                                    </div>
                                    <InputGroup>
                                        <FormControl
                                            placeholder="Search by outlet name..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            aria-label="Search outlet"
                                        />
                                        {searchQuery && (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={clearSearch}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                            </Row>

                            <Row className="mb-2">
                                <Col>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">OUTLET BALANCES</h5>
                                        <Button
                                            variant="outline-primary"
                                            onClick={getOutletsBalance}
                                            disabled={loading}
                                            className='w-25'
                                            size="sm"
                                        >
                                            <i className="fas fa-sync-alt me-2"></i>
                                            {loading ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <div className="mb-3">
                                        <small className="text-muted">
                                            Showing {filteredOutletsBalance.length} of {outletsBalance.length} outlets
                                            {outletFilter !== 'ALL' && ` (${outletFilter} only)`}
                                            {searchQuery && ` matching "${searchQuery}"`}
                                        </small>
                                    </div>

                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 mb-0">Loading outlets balance...</p>
                                        </div>
                                    ) : filteredOutletsBalance.length > 0 ? (
                                        <Row>
                                            {filteredOutletsBalance?.map((outlet, index) => (
                                                <Col key={outlet?.outlet || outlet._id} md={6} lg={4} className="mb-3">
                                                    <Card className="h-100 shadow-sm">
                                                        <Card.Body className="p-3">
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div>
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <Store size={18} className="text-muted me-2" />
                                                                        <h6 className="card-title mb-0 fw-semibold">
                                                                            {outlet?.outlet?.name || outlet.name || 'Unknown Outlet'}
                                                                        </h6>
                                                                        {/* {getStatusBadge(outlet?.outlet?.status || outlet?.status || 'ACTIVE')} */}
                                                                    </div>
                                                                    <p className="text-muted mb-0 small">
                                                                        Current Balance
                                                                    </p>
                                                                </div>
                                                                <Badge
                                                                    bg={getBalanceVariant(outlet.cash_balance)}
                                                                    className="fs-5 px-2 py-1"
                                                                >
                                                                    ₹{formatCurrency(outlet.cash_balance || 0)}
                                                                </Badge>
                                                            </div>

                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleViewTransactions(outlet)}
                                                                    className="flex-fill"
                                                                >
                                                                    <EyeIcon size={16} className="me-1" />
                                                                    View
                                                                </Button>
                                                                {(pettyCashAccess?.includes("PettyCash:add") || email === 'contact@xpansecafe.com') && (
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        onClick={() => handleCreditOutlet(outlet)}
                                                                        className="flex-fill"
                                                                        disabled={(outlet?.outlet?.status || outlet?.status) === 'INACTIVE'}
                                                                        title={(outlet?.outlet?.status || outlet?.status) === 'INACTIVE' ? 'Cannot credit to inactive outlet' : ''}
                                                                    >
                                                                        <CreditCard size={16} className="me-1" />
                                                                        Credit
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted mb-0">
                                                {outletsBalance.length === 0
                                                    ? "No outlets found or no petty cash data available."
                                                    : `No outlets found matching your ${searchQuery ? `search "${searchQuery}"` : 'filters'}.`
                                                }
                                            </p>
                                            {(outletFilter !== 'ALL' || searchQuery) && outletsBalance.length > 0 && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setOutletFilter('ALL');
                                                        setSearchQuery('');
                                                        applyFilters(outletsBalance, 'ALL', '');
                                                    }}
                                                    className="mt-2"
                                                >
                                                    Clear all filters
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </React.Fragment>
                    ) : (
                        /* Transactions View for Selected Outlet */
                        <React.Fragment>
                            {/* Outlet Selection and Balance Card */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Card>
                                        <Card.Body>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Selected Outlet</Form.Label>
                                                <div className="p-2 border rounded bg-light">
                                                    <strong>{selectedOutlet?.label}</strong>
                                                </div>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className={`${skin === 'dark' ? 'bg-dark' : 'bg-primary'} text-white`}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="mb-1">Current Balance</h6>
                                                    <h3 className="mb-0">₹{formatCurrency(outletCurrentBalance)}</h3>
                                                </div>
                                                <div className="fs-1">
                                                    💰
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Actions Section */}
                            <Row className="mb-4">
                                <Col>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => getTransactions()}
                                            disabled={loading}
                                            className='w-25'
                                        >
                                            <i className="fas fa-sync-alt me-2"></i>
                                            {loading ? 'Refreshing...' : 'Refresh'}
                                        </Button>

                                        <Button
                                            variant="outline-success"
                                            className="w-25 fw-semibold"
                                            disabled={downloadBtnLoading || pettyCashTransactions?.length === 0}
                                            onClick={handleDownloadExcel}
                                        >
                                            <FileEarmarkSpreadsheet className=" me-2" />
                                            {downloadBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Download Excel"
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* Transactions Table */}
                            <Row>
                                <Col>
                                    <Card className='mb-3'>
                                        <Card.Header className="d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">Petty Cash Transactions - {selectedOutlet?.label}</h6>
                                        </Card.Header>
                                        <Card.Body>
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                    <p className="mt-2 mb-0">Loading transactions...</p>
                                                </div>
                                            ) : pettyCashTransactions.length > 0 ? (
                                                <div className="table-responsive">
                                                    <Table striped bordered hover>
                                                        <thead>
                                                            <tr>
                                                                <th>Date & Time</th>
                                                                <th className="text-center">Type</th>
                                                                <th className="text-end">Amount</th>
                                                                <th>Remark</th>
                                                                <th>Invoice</th>
                                                                <th>Added By</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {pettyCashTransactions.map((transaction, index) => (
                                                                <tr key={transaction._id || index}>
                                                                    <td>{formatDate(transaction.createdAt)}</td>
                                                                    <td className='text-center'>{getTransactionTypeBadge(transaction.type)}</td>
                                                                    {getAmountDisplay(transaction)}
                                                                    <td style={{ whiteSpace: "normal", wordBreak: "break-word", width: '360px' }}><p>{transaction.remark || '-'}</p></td>
                                                                    <td className="text-center">
                                                                        {transaction.invoice ? (
                                                                            <a
                                                                                href={transaction.invoice}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                            >
                                                                                View
                                                                            </a>
                                                                        ) : (
                                                                            '-'
                                                                        )}
                                                                    </td>
                                                                    <td>{transaction?.outletUserCreatedBy ? transaction?.outletUserCreatedBy?.name : transaction?.adminCreatedBy?.name}</td>
                                                                    <td className="text-center">
                                                                        <button
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => handleDelete(transaction)}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <p className="text-muted mb-0">No transactions found for this outlet.</p>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </React.Fragment>
                    )}
                </div>

                {/* Credit Petty Cash Modal */}
                <Modal show={showCreditModal} onHide={handleCloseCreditModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div className="d-flex align-items-center">
                                <PlusIcon size={22} className="me-2 text-success fw-bold" />
                                Credit Petty Cash To <span className='text-success ms-2'>{creditOutletName || 'Unkown'}</span>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleCreditPettyCash}>
                        <Modal.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Amount (₹) <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="amount"
                                            value={inputData.amount}
                                            onChange={handleInputChange}
                                            placeholder="Enter amount"
                                            min="0.001"
                                            onKeyDown={(e) => ["-", "+", "e", "E"].includes(e.key) && e.preventDefault()}
                                            step="any"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label className='fw-semibold'>Invoice (Optional)</Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                            onChange={handleFileChange}
                                        />
                                        <Form.Text className="text-muted">
                                            Supported formats: PDF, JPG, PNG, DOC
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-semibold'>Remark <span className='text-danger'>*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="remark"
                                    value={inputData.remark}
                                    onChange={handleInputChange}
                                    placeholder="Enter remark for this transaction..."
                                    required
                                />
                            </Form.Group>

                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseCreditModal}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="success"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Crediting...
                                    </>
                                ) : (
                                    'Credit Amount'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Outlet Modal */}
                <Modal show={openDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this transaction? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleSubmitDelete}>
                            {isDeleteLoading ?
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                /> :
                                "Delete Transaction"
                            }
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </React.Fragment>
    )
}

export default PettyCash